import type { Role } from '@/domain/auth'

/**
 * User : projection UI d'un user backend. Le rôle est aligné avec le
 * domaine auth (super_admin | tenant_master | tenant_user).
 */
export interface UserAccount {
    readonly id: string
    readonly tenantId: string
    readonly email: string
    readonly name: string
    readonly role: Role
    readonly disabled: boolean
    readonly createdAt: string
    readonly lastLogin?: string
}

export interface CreateUserInput {
    readonly email: string
    readonly name: string
    readonly role?: Role
    readonly tenantId?: string
    // disabledLogin : crée un compte de service / application (sans login
    // interactif). Le compte ne peut pas se connecter ; il sert à porter
    // des clés en tant qu'owner.
    readonly disabledLogin?: boolean
}

export type UserErrorCode =
    | 'invalid_email'
    | 'invalid_role'
    | 'role_escalation'
    | 'email_already_exists'
    | 'user_not_found'
    | 'tenant_scope'
    | 'csrf_required'
    | 'csrf_invalid'
    | 'unauthorized'
    | 'forbidden'
    | 'invalid_body'
    | 'internal_error'
    | 'network_error'
    | 'unknown'

export class UserError extends Error {
    readonly code: UserErrorCode
    readonly httpStatus: number | null
    constructor(code: UserErrorCode, message: string, httpStatus: number | null = null) {
        super(message)
        this.code = code
        this.httpStatus = httpStatus
        this.name = 'UserError'
    }
}

export function isUserError(err: unknown): err is UserError {
    return err instanceof UserError
}

export const KNOWN_USER_CODES: readonly UserErrorCode[] = [
    'invalid_email',
    'invalid_role',
    'role_escalation',
    'email_already_exists',
    'user_not_found',
    'tenant_scope',
    'csrf_required',
    'csrf_invalid',
    'unauthorized',
    'forbidden',
    'invalid_body',
    'internal_error',
    'network_error',
    'unknown',
] as const
